// Lightweight registry of the city guide pages — slug, display name, dispatching office — in
// display order. This is the ONLY list to edit when a city page is added or reordered.
//
// It lives apart from data/locations.js on purpose: the header/footer (in the shared bundle on
// every route) only need names and paths, not the full page copy. locations.js imports
// CITY_SLUGS from here and fails loudly if a slug has no CITY_PAGES entry.
export const CITY_INDEX = [
  { slug: 'tacoma', city: 'Tacoma', office: 'federal-way' },
  { slug: 'kent', city: 'Kent', office: 'federal-way' },
  { slug: 'auburn', city: 'Auburn', office: 'federal-way' },
  { slug: 'renton', city: 'Renton', office: 'federal-way' },
  { slug: 'puyallup', city: 'Puyallup', office: 'federal-way' },
  { slug: 'seattle', city: 'Seattle', office: 'federal-way' },
  { slug: 'bellevue', city: 'Bellevue', office: 'federal-way' },
  { slug: 'des-moines', city: 'Des Moines', office: 'federal-way' },
  { slug: 'burien', city: 'Burien', office: 'federal-way' },
  { slug: 'everett', city: 'Everett', office: 'snohomish' },
  { slug: 'lynnwood', city: 'Lynnwood', office: 'snohomish' },
  { slug: 'marysville', city: 'Marysville', office: 'snohomish' },
  { slug: 'bothell', city: 'Bothell', office: 'snohomish' },
  { slug: 'mill-creek', city: 'Mill Creek', office: 'snohomish' },
  { slug: 'monroe', city: 'Monroe', office: 'snohomish' },
  { slug: 'lake-stevens', city: 'Lake Stevens', office: 'snohomish' },
];

export const OFFICE_INDEX = [
  { slug: 'federal-way', city: 'Federal Way' },
  { slug: 'snohomish', city: 'Snohomish' },
];

export const CITY_SLUGS = CITY_INDEX.map((c) => c.slug);

/** Display name for any service-area slug (office or city). */
const AREA_NAMES = Object.fromEntries([...OFFICE_INDEX, ...CITY_INDEX].map((a) => [a.slug, a.city]));
export const areaName = (slug) => AREA_NAMES[slug];
export const areaPath = (slug) => `/service-areas/${slug}`;
const AREA_SLUGS = Object.keys(AREA_NAMES);

// Footer "City guides" list: each office followed by the guides it dispatches, as [label, path].
export const FOOTER_CITY_GUIDES = OFFICE_INDEX.flatMap((o) => [
  [o.city, areaPath(o.slug)],
  ...CITY_INDEX.filter((c) => c.office === o.slug).map((c) => [c.city, areaPath(c.slug)]),
]);

/*
 * Footer "Service Areas" section: two sub-headings, every one of the 18 service-area pages, in
 * this fixed order (offices first). Rendered as plain <a href> anchors by the React footer AND the
 * pre-render shell footer so every sitemap URL under /service-areas/ has a sitewide, crawlable link.
 */
const SOUTH_SOUND_ORDER = ['federal-way', 'tacoma', 'kent', 'auburn', 'renton', 'puyallup', 'des-moines', 'burien', 'seattle', 'bellevue'];
const NORTH_SOUND_ORDER = ['snohomish', 'everett', 'lynnwood', 'marysville', 'bothell', 'mill-creek', 'monroe', 'lake-stevens'];
export const FOOTER_SERVICE_AREAS = [
  { heading: 'South Sound', links: SOUTH_SOUND_ORDER.map((s) => [areaName(s), areaPath(s)]) },
  { heading: 'North Sound', links: NORTH_SOUND_ORDER.map((s) => [areaName(s), areaPath(s)]) },
];

/*
 * "Where we install" (SEO Batch B, Part 1.1): every one of the 18 service-area pages grouped the way
 * the owner asked — South Sound / Eastside / North Sound — for the block on the five service pages
 * and the llms.txt service-area list (seo/build-llms.js reads this through jiti). Bellevue and
 * Bothell are the Eastside pages; Bellevue is dispatched from Federal Way and Bothell from
 * Snohomish, which is why this grouping differs from the two-office footer list above.
 */
export const REGION_GROUPS = [
  { heading: 'South Sound', slugs: ['federal-way', 'tacoma', 'kent', 'auburn', 'renton', 'puyallup', 'des-moines', 'burien', 'seattle'] },
  { heading: 'Eastside', slugs: ['bellevue', 'bothell'] },
  { heading: 'North Sound', slugs: ['snohomish', 'everett', 'lynnwood', 'marysville', 'mill-creek', 'monroe', 'lake-stevens'] },
].map((g) => ({ ...g, links: g.slugs.map((s) => [areaName(s), areaPath(s)]) }));

/*
 * "Nearby service areas" — exactly four neighbours per service-area page, shown above the final
 * CTA on every /service-areas/* page (and in its pre-render snapshot). Curated by hand; this is the
 * single source of truth for area-to-area cross-links.
 */
export const NEARBY_AREAS = {
  'federal-way': ['des-moines', 'kent', 'auburn', 'tacoma'],
  tacoma: ['puyallup', 'federal-way', 'auburn', 'des-moines'],
  kent: ['renton', 'auburn', 'federal-way', 'des-moines'],
  auburn: ['kent', 'federal-way', 'puyallup', 'renton'],
  renton: ['kent', 'seattle', 'bellevue', 'burien'],
  puyallup: ['tacoma', 'auburn', 'federal-way', 'kent'],
  'des-moines': ['federal-way', 'burien', 'kent', 'seattle'],
  burien: ['des-moines', 'seattle', 'renton', 'kent'],
  seattle: ['bellevue', 'burien', 'renton', 'bothell'],
  bellevue: ['seattle', 'renton', 'bothell', 'kent'],
  snohomish: ['everett', 'lake-stevens', 'monroe', 'marysville'],
  everett: ['snohomish', 'marysville', 'mill-creek', 'lynnwood'],
  lynnwood: ['bothell', 'mill-creek', 'everett', 'seattle'],
  marysville: ['everett', 'lake-stevens', 'snohomish', 'mill-creek'],
  bothell: ['mill-creek', 'lynnwood', 'bellevue', 'seattle'],
  'mill-creek': ['bothell', 'lynnwood', 'everett', 'snohomish'],
  monroe: ['snohomish', 'lake-stevens', 'bothell', 'everett'],
  'lake-stevens': ['snohomish', 'marysville', 'everett', 'monroe'],
};

/** [label, path] pairs for a page's four nearby areas. */
export const nearbyAreaLinks = (slug) => (NEARBY_AREAS[slug] || []).map((s) => [areaName(s), areaPath(s)]);

// Fail loudly at module load: every service-area page must have exactly four valid, distinct
// neighbours, and the footer groups must cover every page exactly once.
AREA_SLUGS.forEach((slug) => {
  const near = NEARBY_AREAS[slug];
  if (!near || near.length !== 4) throw new Error(`cityIndex.js NEARBY_AREAS.${slug} must list exactly four slugs`);
  near.forEach((n) => { if (!AREA_NAMES[n]) throw new Error(`cityIndex.js NEARBY_AREAS.${slug} references unknown slug "${n}"`); });
  if (near.includes(slug) || new Set(near).size !== 4) throw new Error(`cityIndex.js NEARBY_AREAS.${slug} must be four distinct slugs other than itself`);
});
Object.keys(NEARBY_AREAS).forEach((slug) => { if (!AREA_NAMES[slug]) throw new Error(`cityIndex.js NEARBY_AREAS has unknown page "${slug}"`); });
{
  const covered = [...SOUTH_SOUND_ORDER, ...NORTH_SOUND_ORDER];
  if (new Set(covered).size !== covered.length || covered.length !== AREA_SLUGS.length || covered.some((s) => !AREA_NAMES[s])) {
    throw new Error('cityIndex.js FOOTER_SERVICE_AREAS must list every service-area page exactly once');
  }
  const regions = REGION_GROUPS.flatMap((g) => g.slugs);
  if (new Set(regions).size !== regions.length || regions.length !== AREA_SLUGS.length || regions.some((s) => !AREA_NAMES[s])) {
    throw new Error('cityIndex.js REGION_GROUPS must list every service-area page exactly once');
  }
}
